import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { can } from '../lib/permissions';
import { getUserById, updateUserFields, requestPasswordReset, deleteUserPermanently, getUserNoShows, UserNoShow } from '../api/services/usersService';
import { getVehiclesByOwner } from '../api/services/vehiclesService';
import { getRentalsByRenter, getRentalsByOwner, clearNoShow } from '../api/services/rentalsService';
import { sendCustomNotification } from '../api/services/notificationsService';
import { getAllReviews, deleteReview } from '../api/services/reviewsService';
import { getAllVerifications, updateVerification, fetchVerificationDocument } from '../api/services/verificationsService';
import { VEHICLE_TYPE_LABELS } from '../viewmodels/InternalViewModel';
import OverviewMap from './OverviewMap';
import { Switch } from './ui/dg';

const TYPE_META: Record<string, { icon: string; color: string }> = {
  scooter: { icon: '🛴', color: '#9333EA' },
  motorcycle: { icon: '🏍️', color: '#4F46E5' },
  bike: { icon: '🚴', color: '#10B981' },
  quad: { icon: '🏎️', color: '#F97316' },
  car: { icon: '🚗', color: '#2563EB' },
};

// L'API renvoie les dates du permis en 'YYYY-MM-DD' (colonne DATE) : on les
// formate sans passer par Date, pour éviter le décalage d'un jour que provoque
// l'interprétation UTC d'une date sans heure.
const formatLicenseDate = (value?: string | null): string => {
  if (!value) return '—';
  const [year, month, day] = value.slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

const isLicenseExpired = (value?: string | null): boolean => {
  if (!value) return false;
  return value.slice(0, 10) < new Date().toISOString().slice(0, 10);
};

const normalizeName = (value?: string | null): string =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const VERIFICATION_TYPE_LABELS: Record<string, string> = {
  identity: 'Identité',
  license: 'Permis de conduire',
  address: 'Adresse',
  phone: 'Téléphone',
};

interface User {
  id_user?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  profile_photo?: string | null;
  birth_date?: string;
  role?: string;
  average_rating?: number;
  total_reviews?: number;
  registration_date?: string;
  last_login?: string;
  is_checked?: boolean;
  is_suspended?: boolean;
  // Notifications : ce que la personne veut (app) et ce que l'équipe autorise.
  notify_push?: boolean;
  notify_email?: boolean;
  admin_push_allowed?: boolean;
  admin_email_allowed?: boolean;
  auth_provider?: string;
}

interface UserDetailModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated?: (user: User) => void;
  /** Le compte vient d'être supprimé définitivement : la liste doit l'oublier. */
  onUserDeleted?: (userId: string) => void;
  /** Onglet ouvert d'emblée (le panneau de la liste mène droit aux trajets, aux annonces…). */
  initialTab?: TabId;
  /** Action dépliée d'emblée : « Suspendre » ou « Envoyer une notification » depuis le panneau. */
  initialAction?: 'notify' | 'suspend' | 'delete' | null;
}

const ROLE_LABELS: Record<string, string> = {
  user: 'Utilisateur',
  moderator: 'Modérateur',
  admin: 'Admin'
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Pastille d'état sous le nom (vérifié, connexion, inscription). */
const CHIP = 'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold';
/** Boutons d'action de la fiche : contour gris, l'action principale remplie d'indigo. */
const ACTION_BASE = 'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50';
const ACTION = `${ACTION_BASE} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`;
const ACTION_PRIMARY = `${ACTION_BASE} border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700`;
const ACTION_DANGER = `${ACTION_BASE} border-red-200 bg-white text-red-600 hover:bg-red-50`;
const ACTION_DANGER_SOLID = `${ACTION_BASE} border-red-600 bg-red-600 text-white hover:bg-red-700`;
const ACTION_SUCCESS = `${ACTION_BASE} border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50`;

const PROVIDER_LABELS: Record<string, string> = { google: 'Google', apple: 'Apple', facebook: 'Facebook', email: 'E-mail' };
const providerLabel = (provider?: string | null) => PROVIDER_LABELS[(provider || 'email').toLowerCase()] || provider || 'E-mail';

const formatShortDate = (dateString?: string) => {
  const date = dateString ? new Date(dateString) : null;
  if (!date || isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Une valeur de la fiche, ou « Non renseigné » en gris plutôt qu'un tiret. */
const InfoValue = ({ value, empty = 'Non renseigné' }: { value?: string | null | false; empty?: string }) =>
  value ? <dd className="text-gray-900">{value}</dd> : <dd className="text-gray-400">{empty}</dd>;

/** Interrupteur de la carte Communication ; signale aussi le choix fait par la personne dans l'app. */
const CommunicationToggle = ({ label, on, offInApp, disabled, onToggle }: {
  label: string; on: boolean; offInApp: boolean; disabled: boolean; onToggle: () => void;
}) => (
  <div className="flex items-center justify-between py-1.5">
    <div>
      <p className="text-sm text-gray-800">{label}</p>
      {offInApp && <p className="text-[11px] text-gray-500">Coupé par la personne dans l’app</p>}
    </div>
    <Switch checked={on} onChange={onToggle} label={label} disabled={disabled} />
  </div>
);

const TABS = [
  { id: 'overview', label: 'Aperçu' },
  { id: 'fleet', label: 'Flotte' },
  { id: 'trips', label: 'Trajets' },
  { id: 'reviews', label: 'Avis' },
  { id: 'earnings', label: 'Revenus' },
] as const;

type TabId = typeof TABS[number]['id'];

/* Squelettes des onglets : chaque état de chargement reprend la forme du
   contenu qu'il annonce, plutôt qu'un spinner générique qui oblige la fiche à
   se recomposer entièrement quand la réponse arrive. */
const Line = ({ className = '' }: { className?: string }) => (
  <div className={`animate-pulse rounded bg-gray-100 ${className}`} />
);

const OverviewSkeleton = () => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
    <div className="lg:col-span-2 bg-white rounded-2xl p-5 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
      <Line className="h-4 w-44 mb-4" />
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index}>
            <Line className="h-3 w-24 mb-1.5" />
            <Line className="h-4 w-32 max-w-full" />
          </div>
        ))}
      </dl>
    </div>

    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-5 shadow-[0_1px_2px_rgb(19_21_26/0.04)] grid grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex flex-col items-center">
            <Line className="h-6 w-12 mb-1.5" />
            <Line className="h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const FleetSkeleton = () => (
  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 h-full">
    <Line className="rounded-2xl h-80 lg:h-full" />
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="bg-white rounded-2xl p-4 shadow-[0_1px_2px_rgb(19_21_26/0.04)] flex items-center gap-3">
          <Line className="h-10 w-10 rounded-xl shrink-0" />
          <div className="min-w-0 flex-1">
            <Line className="h-4 w-32 max-w-full mb-1.5" />
            <Line className="h-3 w-20" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TableSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
    <div className="bg-gray-50 px-4 py-2 grid grid-cols-4 gap-4">
      {['w-12', 'w-20', 'w-16', 'w-14'].map((width) => (
        <Line key={width} className={`h-3 ${width}`} />
      ))}
    </div>
    <div className="divide-y divide-gray-100">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="px-4 py-3 grid grid-cols-4 gap-4">
          {['w-20', 'w-28', 'w-14', 'w-16'].map((width) => (
            <Line key={width} className={`h-3.5 ${width} max-w-full`} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

const ReviewsSkeleton = () => (
  <div className="space-y-2">
    {Array.from({ length: 3 }).map((_, index) => (
      <div key={index} className="bg-white rounded-2xl p-4 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
        <div className="flex items-center justify-between mb-2">
          <Line className="h-4 w-24" />
          <Line className="h-3 w-20" />
        </div>
        <Line className="h-3.5 w-3/4" />
      </div>
    ))}
  </div>
);

const EarningsSkeleton = () => (
  <div className="space-y-4">
    <div className="bg-white rounded-2xl p-5 shadow-[0_1px_2px_rgb(19_21_26/0.04)] grid grid-cols-3 gap-4">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="flex flex-col items-center">
          <Line className="h-7 w-24 mb-1.5" />
          <Line className="h-3 w-20" />
        </div>
      ))}
    </div>
    <TableSkeleton />
  </div>
);

const UserDetailModal: React.FC<UserDetailModalProps> = ({ user, isOpen, onClose, onUserUpdated, onUserDeleted, initialTab = 'overview', initialAction = null }) => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);

  const [vehicles, setVehicles] = useState<any[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [rentalsAsRenter, setRentalsAsRenter] = useState<any[]>([]);
  const [rentalsAsOwner, setRentalsAsOwner] = useState<any[]>([]);
  const [reviewsReceived, setReviewsReceived] = useState<any[]>([]);
  const [verifications, setVerifications] = useState<any[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [deletingReviewId, setDeletingReviewId] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const removeReview = async (review: any) => {
    if (!token) return;
    if (!window.confirm('Supprimer cet avis ? Cette action est irréversible.')) return;
    setDeletingReviewId(review.id_review);
    setReviewError(null);
    try {
      await deleteReview(review.id_review, token);
      setReviewsReceived((prev) => prev.filter((r) => r.id_review !== review.id_review));
    } catch (err: any) {
      setReviewError(err.message || "Échec de la suppression de l'avis.");
    } finally {
      setDeletingReviewId(null);
    }
  };
  const [verificationBusyId, setVerificationBusyId] = useState<string | null>(null);
  // Vignettes des documents, téléchargées avec le jeton puis exposées en blob:
  // une <img src> ne sait pas envoyer d'en-tête Authorization.
  const [docUrls, setDocUrls] = useState<Record<string, string>>({});
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  // Une URL peut être morte — avatar d'un fournisseur externe, fichier effacé —
  // auquel cas l'initiale reprend la main plutôt que l'icône d'image cassée.
  const [photoBroken, setPhotoBroken] = useState(false);
  // Un modérateur consulte une fiche pour rattacher un dossier à une personne ;
  // il n'agit pas sur le compte. Les boutons correspondants ne lui sont pas
  // grisés mais absents : un bouton désactivé sans explication est une
  // frustration, pas une information.
  const { user: currentUser } = useAuth();
  const canVerify = can(currentUser?.role, 'user.verify');
  const canSuspend = can(currentUser?.role, 'user.suspend');
  // Supprimer un compte est irréversible et sans préavis : admin seulement,
  // et jamais son propre compte depuis cette fiche.
  const canDelete = can(currentUser?.role, 'user.delete') && currentUser?.id_user !== user?.id_user;

  // Rendez-vous manqués reprochés au compte : l'équipe tranche les
  // contestations ici (retirer une absence à tort), et lève la suspension
  // avec « Réactiver le compte ».
  const [noShows, setNoShows] = useState<UserNoShow[]>([]);
  const [noShowsRecent, setNoShowsRecent] = useState(0);
  const [clearingNoShowId, setClearingNoShowId] = useState<string | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', phone: '' });

  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyBody, setNotifyBody] = useState('');

  const [suspendOpen, setSuspendOpen] = useState(false);
  const dangerRef = useRef<HTMLDivElement>(null);
  // Suspension et suppression se décident dans la « Zone sensible » de l'aperçu :
  // y aller et l'amener à l'écran quand l'un des deux s'ouvre, aussi depuis la liste.
  useEffect(() => {
    if (!suspendOpen && !deleteOpen) return;
    setActiveTab('overview');
    const frame = requestAnimationFrame(() => dangerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
    return () => cancelAnimationFrame(frame);
  }, [suspendOpen, deleteOpen, loading]);
  const [suspendReason, setSuspendReason] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab(initialTab);
    setEditing(false);
    setNotifyOpen(initialAction === 'notify');
    setSuspendOpen(initialAction === 'suspend');
    setDeleteOpen(initialAction === 'delete');
    setDeleteConfirm('');
    setToast(null);
    setNoShows([]);
    setNoShowsRecent(0);

    if (user?.id_user && token) {
      setLoading(true);
      getUserById(user.id_user, token)
        .then((data: any) => setUserData(data))
        .catch(() => setUserData(user))
        .finally(() => setLoading(false));
    } else if (user) {
      setUserData(user);
    }
    // initialTab / initialAction ne comptent qu'à l'ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user, token]);

  useEffect(() => {
    if (!isOpen || !userData?.id_user || !token) return;
    getUserNoShows(userData.id_user, token)
      .then((data) => {
        setNoShows(Array.isArray(data.no_shows) ? data.no_shows : []);
        setNoShowsRecent(data.recent_count || 0);
      })
      .catch(() => { setNoShows([]); setNoShowsRecent(0); });
  }, [isOpen, userData?.id_user, token]);

  useEffect(() => {
    if (!isOpen || !userData?.id_user || !token) return;
    setLoadingRelated(true);
    Promise.all([
      getVehiclesByOwner(userData.id_user, token).catch(() => []),
      getRentalsByRenter(userData.id_user, token).catch(() => []),
      getRentalsByOwner(userData.id_user, token).catch(() => []),
      getAllReviews(token).catch(() => []),
      getAllVerifications(token).catch(() => []),
    ]).then(([v, renter, owner, reviews, allVerifications]) => {
      setVehicles(Array.isArray(v) ? v : []);
      setRentalsAsRenter(Array.isArray(renter) ? renter : []);
      setRentalsAsOwner(Array.isArray(owner) ? owner : []);
      setReviewsReceived(
        (Array.isArray(reviews) ? reviews : []).filter((r: any) => r.target_id === userData.id_user)
      );
      setVerifications(
        (Array.isArray(allVerifications) ? allVerifications : []).filter((v: any) => v.user_id === userData.id_user)
      );
    }).finally(() => setLoadingRelated(false));
  }, [isOpen, userData?.id_user, token]);

  // Vignettes des pièces déposées. Les images ne sont servies que derrière le
  // jeton, on les télécharge donc une fois puis on révoque les URL au démontage
  // pour ne pas garder les documents en mémoire plus longtemps que l'écran.
  useEffect(() => {
    if (!token) return;
    const pending = verifications.filter((v) => v.status === 'pending');
    if (pending.length === 0) return;

    let cancelled = false;
    const created: string[] = [];

    (async () => {
      for (const verification of pending) {
        const sides: Array<'front' | 'back'> = ['front', 'back'];
        for (const side of sides) {
          const stored = side === 'front' ? verification.document_front_url : verification.document_back_url;
          if (!stored) continue;
          try {
            const url = await fetchVerificationDocument(verification.id_verification, side, token);
            if (cancelled) {
              URL.revokeObjectURL(url);
              return;
            }
            created.push(url);
            setDocUrls((prev) => ({ ...prev, [`${verification.id_verification}:${side}`]: url }));
          } catch {
            // Document illisible ou supprimé du disque : la vignette reste
            // absente, l'admin voit quand même la ligne et peut décider.
          }
        }
      }
    })();

    return () => {
      cancelled = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [verifications, token]);

  const earnings = useMemo(() => {
    const completed = rentalsAsOwner.filter((r) => r.status === 'completed');
    const total = completed.reduce((sum, r) => sum + (Number(r.total_price) || 0), 0);
    return { total, completedCount: completed.length, allCount: rentalsAsOwner.length };
  }, [rentalsAsOwner]);

  const applyUpdate = async (data: Record<string, any>, successMessage: string, busyKey: string) => {
    if (!userData?.id_user || !token) return;
    setBusy(busyKey);
    setToast(null);
    try {
      const updated = await updateUserFields(userData.id_user, data, token);
      setUserData((prev) => ({ ...prev, ...updated }));
      onUserUpdated?.({ ...userData, ...updated });
      setToast({ message: successMessage, type: 'success' });
    } catch (err: any) {
      setToast({ message: err.message || 'Échec de la mise à jour.', type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const toggleVerified = () => applyUpdate(
    { is_checked: !userData?.is_checked },
    userData?.is_checked ? 'Vérification retirée.' : 'Compte vérifié.',
    'verify'
  );

  const changeRole = (role: string) => applyUpdate({ role }, `Rôle changé en ${ROLE_LABELS[role]}.`, 'role');

  const startEditing = () => {
    setEditForm({
      first_name: userData?.first_name || '',
      last_name: userData?.last_name || '',
      phone: userData?.phone || ''
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    await applyUpdate(editForm, 'Profil mis à jour.', 'edit');
    setEditing(false);
  };

  const doResetPassword = async () => {
    if (!userData?.email) return;
    setBusy('reset');
    setToast(null);
    try {
      await requestPasswordReset(userData.email);
      setToast({ message: `Email de réinitialisation envoyé à ${userData.email}.`, type: 'success' });
    } catch (err: any) {
      setToast({ message: err.message || 'Échec de l\'envoi.', type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const doSendNotification = async () => {
    if (!userData?.id_user || !token || !notifyTitle.trim() || !notifyBody.trim()) return;
    setBusy('notify');
    setToast(null);
    try {
      const res = await sendCustomNotification(
        { title: notifyTitle.trim(), body: notifyBody.trim(), target: 'user', userId: userData.id_user },
        token
      );
      setToast({ message: res.message || 'Notification envoyée.', type: res.failed ? 'error' : 'success' });
      setNotifyOpen(false);
      setNotifyTitle('');
      setNotifyBody('');
    } catch (err: any) {
      setToast({ message: err.message || 'Échec de l\'envoi.', type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const doSuspend = async () => {
    await applyUpdate({ is_suspended: true }, 'Compte suspendu.', 'suspend');
    setSuspendOpen(false);
    setSuspendReason('');
  };

  const doReactivate = () => applyUpdate({ is_suspended: false }, 'Compte réactivé.', 'suspend');

  // L'équipe décide si ce compte peut recevoir des pushs / des e-mails. Le
  // choix de la personne (Paramètres de l'app) reste à part : un canal n'est
  // utilisé que si les deux disent oui.
  const toggleAdminPush = () => applyUpdate(
    { admin_push_allowed: userData?.admin_push_allowed === false },
    userData?.admin_push_allowed === false ? 'Pushs de nouveau autorisés.' : 'Pushs coupés pour ce compte.',
    'notif'
  );
  const toggleAdminEmail = () => applyUpdate(
    { admin_email_allowed: userData?.admin_email_allowed === false },
    userData?.admin_email_allowed === false ? 'E-mails de nouveau autorisés.' : 'E-mails coupés pour ce compte.',
    'notif'
  );

  const doClearNoShow = async (rentalId: string) => {
    if (!token) return;
    if (!window.confirm("Retirer cette absence du compte ? La location reste annulée ; l'absence ne lui sera plus comptée.")) return;
    setClearingNoShowId(rentalId);
    setToast(null);
    try {
      const res = await clearNoShow(rentalId, token);
      setNoShows((prev) => prev.filter((n) => n.id_rental !== rentalId));
      setNoShowsRecent(res.absent_no_show_count ?? Math.max(0, noShowsRecent - 1));
      setToast({ message: 'Absence retirée.', type: 'success' });
    } catch (err: any) {
      setToast({ message: err.message || "Retrait de l'absence impossible.", type: 'error' });
    } finally {
      setClearingNoShowId(null);
    }
  };

  const doDelete = async () => {
    if (!userData?.id_user || !token) return;
    setBusy('delete');
    setToast(null);
    try {
      const res = await deleteUserPermanently(userData.id_user, token);
      const removed = res.removed;
      const summary = removed
        ? ` (${removed.vehicles} annonce(s), ${removed.rentals} location(s), ${removed.reviews} avis)`
        : '';
      onUserDeleted?.(userData.id_user);
      window.alert(`Compte supprimé définitivement${summary}.`);
      onClose();
    } catch (err: any) {
      setToast({ message: err.message || 'Suppression impossible.', type: 'error' });
    } finally {
      setBusy(null);
      setDeleteOpen(false);
      setDeleteConfirm('');
    }
  };

  const reviewVerification = async (
    verificationId: string,
    status: 'approved' | 'rejected',
    adminNotes?: string
  ) => {
    if (!token) return;
    setVerificationBusyId(verificationId);
    try {
      const updated = await updateVerification(verificationId, { status, admin_notes: adminNotes }, token);
      setVerifications((prev) => prev.map((v) => (v.id_verification === verificationId ? updated : v)));
      // Une identité/permis approuvé vaut vérification de compte.
      if (status === 'approved' && !userData?.is_checked) {
        await applyUpdate({ is_checked: true }, 'Vérification approuvée — compte vérifié.', 'verify');
      } else {
        setToast({ message: status === 'approved' ? 'Vérification approuvée.' : 'Vérification rejetée.', type: 'success' });
      }
    } catch (err: any) {
      setToast({ message: err.message || 'Échec de la mise à jour.', type: 'error' });
    } finally {
      setVerificationBusyId(null);
      setRejectingId(null);
      setRejectReason('');
    }
  };

  if (!isOpen || !user) return null;

  const pendingVerifications = verifications.filter((v) => v.status === 'pending');

  const fullName = `${userData?.first_name || user.first_name || ''} ${userData?.last_name || user.last_name || ''}`.trim() || 'Utilisateur';

  // Signalé, jamais bloquant : un nom d'usage, un prénom composé ou une
  // translittération différente expliquent la plupart des divergences. C'est à
  // l'admin de trancher en regardant l'image.
  const holderMismatch = (license: any): boolean => {
    const declaredFirst = normalizeName(license?.holder_first_name);
    const declaredLast = normalizeName(license?.holder_last_name);
    if (!declaredFirst && !declaredLast) return false;
    const accountFirst = normalizeName(userData?.first_name || user.first_name);
    const accountLast = normalizeName(userData?.last_name || user.last_name);
    return declaredFirst !== accountFirst || declaredLast !== accountLast;
  };
  const initial = fullName.charAt(0).toUpperCase();
  const profilePhoto = userData?.profile_photo || user.profile_photo;

  const mapVehicles = vehicles
    .filter((v) => v.latitude && v.longitude)
    .map((v) => ({
      id: v.id_vehicle,
      title: [v.brand, v.model].filter(Boolean).join(' ') || v.serial_number || 'Véhicule',
      type: v.vehicle_type,
      latitude: Number(v.latitude),
      longitude: Number(v.longitude),
      is_available: v.is_available,
      hourly_rate: Number(v.hourly_rate) || 0,
      year: v.year,
      isPlatformOwned: v.is_platform_owned,
      raw: v,
      plate: v.serial_number || null,
      typeLabel: VEHICLE_TYPE_LABELS[v.vehicle_type] || v.vehicle_type,
      state: v.is_suspended ? { label: 'Suspendue', tone: 'red' as const }
        : !v.is_available ? { label: 'En pause', tone: 'grey' as const }
        : { label: 'En ligne', tone: 'green' as const },
      // `hourly_rate` porte le prix du JOUR des anciennes annonces.
      prices: [
        ...((Number(v.daily_rate) || (Number(v.price_per_hour) ? 0 : Number(v.hourly_rate))) > 0
          ? [{ value: `${(Number(v.daily_rate) || Number(v.hourly_rate)).toFixed(0)} DT`, unit: 'par jour' }] : []),
        ...(Number(v.price_per_hour) > 0 ? [{ value: `${Number(v.price_per_hour).toFixed(0)} DT`, unit: 'par heure' }] : []),
      ],
    }));

  const selectedVehicle = vehicles.find((v) => v.id_vehicle === selectedVehicleId) || null;

  const tabCount = (id: TabId): number | null => {
    if (loadingRelated) return null;
    if (id === 'fleet') return vehicles.length;
    if (id === 'trips') return rentalsAsRenter.length;
    if (id === 'reviews') return reviewsReceived.length;
    return null;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-3 sm:p-6">
      <div className="w-full h-full sm:h-[88vh] sm:max-w-7xl bg-white rounded-none sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-gray-100 flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <span className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-lg font-semibold text-white">
              {initial}
              {profilePhoto && !photoBroken && (
                <img
                  src={profilePhoto}
                  alt={fullName}
                  onError={() => setPhotoBroken(true)}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900 truncate">{fullName}</h2>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                  userData?.role === 'admin' ? 'bg-emerald-50 text-emerald-700' :
                  userData?.role === 'moderator' ? 'bg-orange-50 text-orange-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {ROLE_LABELS[userData?.role || 'user']}
                </span>
              </div>
              <p className="text-sm text-gray-500 truncate">{userData?.email}</p>
              {/* L'état du compte d'un coup d'œil, au lieu de boutons qui le portaient. */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {userData?.is_suspended ? (
                  <span className={`${CHIP} border-red-200 bg-red-50 text-red-700`}>Suspendu</span>
                ) : userData?.is_checked ? (
                  <span className={`${CHIP} border-emerald-200 bg-emerald-50 text-emerald-700`}>Compte vérifié</span>
                ) : (
                  <span className={`${CHIP} border-amber-200 bg-amber-50 text-amber-700`}>Compte non vérifié</span>
                )}
                <span className={`${CHIP} border-gray-200 bg-gray-50 text-gray-600`}>{providerLabel(userData?.auth_provider)}</span>
                {userData?.registration_date && (
                  <span className={`${CHIP} border-gray-200 bg-gray-50 text-gray-600`}>Inscrit le {formatShortDate(userData.registration_date)}</span>
                )}
                {userData?.last_login && (
                  <span className={`${CHIP} border-gray-200 bg-gray-50 text-gray-600`}>Vu le {formatDateTime(userData.last_login)}</span>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {toast && (
          <div className={`px-5 sm:px-6 py-2 text-sm ${toast.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {toast.message}
          </div>
        )}

        {/* Actions courantes seulement : les réglages vont dans l'aperçu, la suspension
            et la suppression dans sa « Zone sensible ». */}
        <div className="px-5 sm:px-6 py-3.5 border-b border-gray-100 flex flex-wrap items-center gap-2">
          {canVerify && (
            <button
              type="button"
              disabled={busy === 'verify'}
              onClick={toggleVerified}
              className={userData?.is_checked ? ACTION : ACTION_PRIMARY}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {userData?.is_checked ? 'Retirer la vérification' : 'Vérifier le compte'}
            </button>
          )}

          <button type="button" onClick={startEditing} className={ACTION}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Modifier
          </button>

          <button type="button" onClick={() => setNotifyOpen((v) => !v)} className={ACTION}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.73 21a2 2 0 01-3.46 0" />
            </svg>
            Notifier
          </button>

          <button
            type="button"
            disabled={busy === 'reset'}
            onClick={doResetPassword}
            title="Envoie un e-mail de réinitialisation du mot de passe"
            className={ACTION}
          >
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Mot de passe
          </button>

          {/* Changer un rôle, c'est distribuer des droits : aux admins seuls. */}
          {canSuspend && (
          <div className="ml-auto inline-flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Rôle</span>
            <select
              value={userData?.role || 'user'}
              disabled={busy === 'role'}
              onChange={(e) => changeRole(e.target.value)}
              className="text-sm font-medium pl-2.5 pr-1.5 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-800 disabled:opacity-50"
            >
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          )}
        </div>

        {notifyOpen && (
          <div className="px-5 sm:px-6 py-3 border-b border-gray-100 bg-indigo-50/40 space-y-2">
            <input
              type="text"
              value={notifyTitle}
              onChange={(e) => setNotifyTitle(e.target.value)}
              placeholder="Titre"
              maxLength={80}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <textarea
              value={notifyBody}
              onChange={(e) => setNotifyBody(e.target.value)}
              placeholder="Message"
              maxLength={240}
              rows={2}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setNotifyOpen(false)} className="text-xs text-gray-500 px-3 py-1.5">Annuler</button>
              <button
                type="button"
                disabled={busy === 'notify' || !notifyTitle.trim() || !notifyBody.trim()}
                onClick={doSendNotification}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                Envoyer
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="px-5 sm:px-6 border-b border-gray-100 flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab.label}
              {tabCount(tab.id) !== null && (
                <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-500">{tabCount(tab.id)}</span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 bg-gray-50">
          {loading ? (
            <OverviewSkeleton />
          ) : activeTab === 'overview' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-white rounded-2xl p-5 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">Informations personnelles</h3>
                  {editing && (
                    <div className="flex gap-2">
                      <button onClick={() => setEditing(false)} className="text-xs text-gray-500">Annuler</button>
                      <button
                        onClick={saveEdit}
                        disabled={busy === 'edit'}
                        className="text-xs font-medium px-2.5 py-1 rounded-lg bg-indigo-600 text-white disabled:opacity-50"
                      >
                        Enregistrer
                      </button>
                    </div>
                  )}
                </div>

                {editing ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500">Prénom</label>
                      <input
                        value={editForm.first_name}
                        onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))}
                        className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Nom</label>
                      <input
                        value={editForm.last_name}
                        onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))}
                        className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-gray-500">Téléphone</label>
                      <input
                        value={editForm.phone}
                        onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                        className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                ) : (
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div><dt className="text-xs text-gray-500">E-mail</dt><InfoValue value={userData?.email} /></div>
                    <div><dt className="text-xs text-gray-500">Téléphone</dt><InfoValue value={userData?.phone} /></div>
                    <div><dt className="text-xs text-gray-500">Date de naissance</dt><InfoValue value={userData?.birth_date && formatDate(userData.birth_date)} empty="Non renseignée" /></div>
                    <div><dt className="text-xs text-gray-500">Connexion</dt><InfoValue value={providerLabel(userData?.auth_provider)} /></div>
                    <div><dt className="text-xs text-gray-500">Inscrit le</dt><InfoValue value={userData?.registration_date && formatDate(userData.registration_date)} /></div>
                    <div><dt className="text-xs text-gray-500">Dernière connexion</dt><InfoValue value={userData?.last_login && formatDateTime(userData.last_login)} empty="Jamais" /></div>
                  </dl>
                )}
              </div>

              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-4 shadow-[0_1px_2px_rgb(19_21_26/0.04)] grid grid-cols-2 gap-2">
                  {[
                    { value: String(rentalsAsRenter.length), label: 'Trajets' },
                    { value: String(vehicles.length), label: 'Mobilités' },
                    {
                      value: userData?.average_rating && Number(userData?.total_reviews) > 0 ? Number(userData.average_rating).toFixed(1) : 'Aucune',
                      label: Number(userData?.total_reviews) > 0 ? `Note (${userData?.total_reviews} avis)` : 'Note',
                    },
                    { value: `${earnings.total.toFixed(0)} TND`, label: 'Revenus' },
                  ].map((kpi) => (
                    <div key={kpi.label} className="rounded-xl bg-gray-50 px-3 py-2.5">
                      <p className="text-lg font-semibold text-gray-900">{kpi.value}</p>
                      <p className="text-xs text-gray-500">{kpi.label}</p>
                    </div>
                  ))}
                </div>

                {canSuspend && (
                  <div className="bg-white rounded-2xl p-5 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Communication</h3>
                    <p className="text-[11px] text-gray-500 mb-2">Ce que Dingo peut envoyer à cette personne.</p>
                    <CommunicationToggle
                      label="Notifications push"
                      on={userData?.admin_push_allowed !== false}
                      offInApp={userData?.notify_push === false}
                      disabled={busy === 'notif'}
                      onToggle={toggleAdminPush}
                    />
                    <CommunicationToggle
                      label="E-mails"
                      on={userData?.admin_email_allowed !== false}
                      offInApp={userData?.notify_email === false}
                      disabled={busy === 'notif'}
                      onToggle={toggleAdminEmail}
                    />
                  </div>
                )}

                {noShows.length > 0 && (
                  <div className="bg-white rounded-2xl p-5 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">Rendez-vous manqués</h3>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${noShowsRecent >= 3 ? 'bg-red-100 text-red-700' : noShowsRecent >= 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                        {noShowsRecent} sur 90 j
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-3">
                      2 absences en 90 jours = avertissement, 3 = suspension. Une absence contestée à raison se retire ici ;
                      la suspension se lève avec « Réactiver le compte ».
                    </p>
                    <div className="space-y-2">
                      {noShows.map((n) => {
                        const vehicle = [n.vehicle_brand, n.vehicle_model].filter(Boolean).join(' ') || 'Véhicule';
                        const reporter = [n.reporter_first_name, n.reporter_last_name].filter(Boolean).join(' ') || (n.absent_role === 'renter' ? 'le propriétaire' : 'le locataire');
                        return (
                          <div key={n.id_rental} className={`rounded-xl border p-3 ${n.no_show_contested_at ? 'border-orange-200 bg-orange-50/50' : 'border-gray-100 bg-gray-50/60'}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-gray-900 truncate">
                                  {vehicle} · {n.absent_role === 'renter' ? 'en tant que locataire' : 'en tant que propriétaire'}
                                </p>
                                <p className="text-[11px] text-gray-500">
                                  Rendez-vous du {new Date(n.start_date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} ·
                                  déclaré par {reporter} le {new Date(n.no_show_reported_at).toLocaleDateString('fr-FR')}
                                </p>
                                {n.cancellation_reason && (
                                  <p className="text-[11px] text-gray-600 mt-1">{n.cancellation_reason}</p>
                                )}
                                {n.no_show_contested_at && (
                                  <p className="text-[11px] text-orange-700 font-medium mt-1">
                                    Contestée le {new Date(n.no_show_contested_at).toLocaleDateString('fr-FR')} — voir la discussion d’assistance.
                                  </p>
                                )}
                              </div>
                              {canSuspend && (
                                <button
                                  type="button"
                                  disabled={clearingNoShowId === n.id_rental}
                                  onClick={() => doClearNoShow(n.id_rental)}
                                  className="shrink-0 text-[11px] font-medium px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                >
                                  {clearingNoShowId === n.id_rental ? '…' : "Retirer l'absence"}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {pendingVerifications.length > 0 && (
                  <div className="bg-white rounded-2xl p-5 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Vérifications en attente</h3>
                    <div className="space-y-3">
                      {pendingVerifications.map((v) => (
                        <div key={v.id_verification} className="rounded-xl border border-orange-100 bg-orange-50/50 p-3">
                          <p className="text-xs font-semibold text-gray-900 mb-2">{VERIFICATION_TYPE_LABELS[v.type] || v.type}</p>

                          {v.driver_license && (
                            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 mb-2 text-[11px]">
                              <dt className="text-gray-500">Numéro</dt>
                              <dd className="text-gray-900 font-medium">{v.driver_license.license_number}</dd>
                              <dt className="text-gray-500">Catégories</dt>
                              <dd className="text-gray-900 font-medium">{(v.driver_license.categories || []).join(', ') || '—'}</dd>
                              <dt className="text-gray-500">Délivré le</dt>
                              <dd className="text-gray-900">{formatLicenseDate(v.driver_license.issue_date)}</dd>
                              <dt className="text-gray-500">Expire le</dt>
                              <dd className={isLicenseExpired(v.driver_license.expiry_date) ? 'text-red-600 font-semibold' : 'text-gray-900'}>
                                {formatLicenseDate(v.driver_license.expiry_date)}
                                {isLicenseExpired(v.driver_license.expiry_date) ? ' — expiré' : ''}
                              </dd>
                              <dt className="text-gray-500">Titulaire déclaré</dt>
                              <dd className="text-gray-900">
                                {[v.driver_license.holder_first_name, v.driver_license.holder_last_name].filter(Boolean).join(' ') || '—'}
                              </dd>
                            </dl>
                          )}

                          {/* Le nom lu sur le permis doit correspondre au compte : une
                              divergence est le premier signe d'un document emprunté. */}
                          {v.driver_license && holderMismatch(v.driver_license) && (
                            <p className="text-[11px] text-red-600 mb-2">
                              ⚠️ Le titulaire déclaré ne correspond pas au nom du compte ({fullName}).
                            </p>
                          )}

                          {(v.document_front_url || v.document_back_url) && (
                            <div className="flex gap-2 mb-2">
                              {(['front', 'back'] as const).map((side) => {
                                const stored = side === 'front' ? v.document_front_url : v.document_back_url;
                                if (!stored) return null;
                                const url = docUrls[`${v.id_verification}:${side}`];
                                return (
                                  <a
                                    key={side}
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={`block w-24 h-16 rounded-lg overflow-hidden border border-orange-200 bg-white ${url ? '' : 'animate-pulse'}`}
                                    title={side === 'front' ? 'Recto' : 'Verso'}
                                  >
                                    {url && <img src={url} alt={side === 'front' ? 'Recto' : 'Verso'} className="w-full h-full object-cover" />}
                                  </a>
                                );
                              })}
                            </div>
                          )}

                          {rejectingId === v.id_verification ? (
                            <div className="space-y-2">
                              <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={2}
                                placeholder="Motif du refus (envoyé à l'utilisateur)"
                                className="w-full text-[11px] rounded-lg border border-red-200 p-2 focus:outline-none focus:ring-1 focus:ring-red-300"
                              />
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  disabled={verificationBusyId === v.id_verification || rejectReason.trim().length === 0}
                                  onClick={() => reviewVerification(v.id_verification, 'rejected', rejectReason.trim())}
                                  className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                >
                                  Confirmer le refus
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setRejectingId(null); setRejectReason(''); }}
                                  className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                                >
                                  Annuler
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                disabled={verificationBusyId === v.id_verification}
                                onClick={() => reviewVerification(v.id_verification, 'approved')}
                                className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                              >
                                Approuver
                              </button>
                              <button
                                type="button"
                                disabled={verificationBusyId === v.id_verification}
                                onClick={() => { setRejectingId(v.id_verification); setRejectReason(''); }}
                                className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                              >
                                Rejeter
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {(canSuspend || canDelete) && (
                <div ref={dangerRef} className="lg:col-span-3 bg-white rounded-2xl border border-red-100 overflow-hidden">
                  <div className="p-5 flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[14rem]">
                      <h3 className="text-sm font-semibold text-red-700">Zone sensible</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        La suspension bloque le compte en lecture seule. La suppression efface tout, sans retour.
                      </p>
                    </div>
                    {canSuspend && (userData?.is_suspended ? (
                      <button
                        type="button"
                        disabled={busy === 'suspend'}
                        onClick={doReactivate}
                        className={ACTION_SUCCESS}
                      >
                        Réactiver le compte
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setSuspendOpen((v) => !v); setDeleteOpen(false); }}
                        className={ACTION_DANGER}
                      >
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="9" strokeLinecap="round" />
                          <path strokeLinecap="round" d="M8 8l8 8" />
                        </svg>
                        Suspendre
                      </button>
                    ))}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => { setDeleteOpen((v) => !v); setDeleteConfirm(''); setSuspendOpen(false); }}
                        className={ACTION_DANGER_SOLID}
                      >
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
                        </svg>
                        Supprimer
                      </button>
                    )}
                  </div>
        {suspendOpen && (
          <div className="px-5 py-3 border-t border-gray-100 bg-red-50/60 space-y-2">
            <p className="text-xs text-red-700">Le compte sera bloqué à la connexion, immédiatement.</p>
            <input
              type="text"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Motif (optionnel)"
              className="w-full px-3 py-1.5 text-sm border border-red-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setSuspendOpen(false)} className="text-xs text-gray-500 px-3 py-1.5">Annuler</button>
              <button
                type="button"
                disabled={busy === 'suspend'}
                onClick={doSuspend}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                Confirmer la suspension
              </button>
            </div>
          </div>
        )}

        {deleteOpen && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-900 text-white space-y-2">
            <p className="text-xs">
              Suppression <strong>définitive et immédiate</strong> : le compte, ses annonces et leurs photos, ses locations
              (des deux côtés), ses avis, son permis et ses discussions disparaissent de la base. Aucun message n’est envoyé à la personne.
              Rien ne se récupère ensuite.
            </p>
            <p className="text-xs text-gray-300">Pour confirmer, tapez l’e-mail du compte : <span className="font-mono text-white">{userData?.email || '—'}</span></p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="E-mail du compte"
              className="w-full px-3 py-1.5 text-sm rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteOpen(false)} className="text-xs text-gray-300 px-3 py-1.5">Annuler</button>
              <button
                type="button"
                disabled={busy === 'delete' || !userData?.email || deleteConfirm.trim().toLowerCase() !== String(userData.email).trim().toLowerCase()}
                onClick={doDelete}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-40"
              >
                {busy === 'delete' ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        )}

                </div>
              )}
            </div>
          ) : activeTab === 'fleet' ? (
            loadingRelated ? (
              <FleetSkeleton />
            ) : vehicles.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm text-gray-500">Cet utilisateur ne propose aucune mobilité à la location.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 h-full">
                <div className="rounded-2xl overflow-hidden h-80 lg:h-full">
                  <OverviewMap
                    vehicles={mapVehicles}
                    mode="select"
                    actionLabel="Voir dans la liste"
                    onVehicleClick={(v) => setSelectedVehicleId(v.id)}
                    onVehicleSelect={selectedVehicle ? {
                      latitude: Number(selectedVehicle.latitude),
                      longitude: Number(selectedVehicle.longitude)
                    } : undefined}
                  />
                </div>
                <div className="space-y-1.5 max-h-80 lg:max-h-full overflow-y-auto bg-white rounded-2xl p-2 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
                  {vehicles.map((v) => {
                    const meta = TYPE_META[v.vehicle_type] || { icon: '🚗', color: '#4F46E5' };
                    const title = [v.brand, v.model].filter(Boolean).join(' ') || v.serial_number || 'Véhicule';
                    const isSelected = v.id_vehicle === selectedVehicleId;
                    return (
                      <div
                        key={v.id_vehicle}
                        onClick={() => setSelectedVehicleId(v.id_vehicle)}
                        className={`flex items-center gap-3 px-2.5 py-2.5 rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                        } ${!v.is_available ? 'opacity-50' : ''}`}
                      >
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0"
                          style={{ backgroundColor: `${meta.color}1A` }}
                        >
                          {meta.icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="font-semibold text-gray-900 text-sm truncate">{title}</p>
                            {v.is_suspended && (
                              <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-700">Suspendu</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 truncate">{VEHICLE_TYPE_LABELS[v.vehicle_type] || v.vehicle_type}</p>
                        </div>
                        {v.hourly_rate && (
                          <span className="text-xs font-semibold text-gray-700 flex-shrink-0">{Number(v.hourly_rate).toFixed(0)} TND</span>
                        )}
                        <div
                          className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            v.is_available ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {v.is_available ? (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          ) : activeTab === 'trips' ? (
            loadingRelated ? (
              <TableSkeleton />
            ) : rentalsAsRenter.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm text-gray-500">Cet utilisateur n'a pas encore effectué de trajet.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl overflow-hidden shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Véhicule</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Montant</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rentalsAsRenter.map((r) => (
                      <tr key={r.id_rental}>
                        <td className="px-4 py-2.5 text-sm text-gray-700">{formatDateTime(r.start_date || r.created_at)}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-700">{VEHICLE_TYPE_LABELS[r.vehicle_type] || r.vehicle_type || '—'}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-700">{r.total_price ? `${r.total_price} TND` : '—'}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            r.status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                            r.status === 'active' ? 'bg-indigo-50 text-indigo-700' :
                            r.status === 'cancelled' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {r.status === 'completed' ? 'Terminé' : r.status === 'active' ? 'En cours' : r.status === 'cancelled' ? 'Annulé' : r.status === 'pending' ? 'À venir' : r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : activeTab === 'reviews' ? (
            loadingRelated ? (
              <ReviewsSkeleton />
            ) : reviewsReceived.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm text-gray-500">Aucun avis reçu pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {reviewError && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{reviewError}</div>}
                {reviewsReceived.map((r) => (
                  <div key={r.id_review} className="bg-white rounded-2xl p-4 shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-amber-500 text-sm">{'★'.repeat(Math.max(0, Math.min(5, Math.round(r.rating))))}<span className="text-gray-200">{'★'.repeat(5 - Math.max(0, Math.min(5, Math.round(r.rating))))}</span></span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{formatDate(r.review_date || r.created_at)}</span>
                        <button
                          type="button"
                          disabled={deletingReviewId === r.id_review}
                          onClick={() => removeReview(r)}
                          className="text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md px-1.5 py-0.5 transition-colors disabled:opacity-50"
                        >
                          {deletingReviewId === r.id_review ? '...' : 'Supprimer'}
                        </button>
                      </div>
                    </div>
                    {r.comment && <p className="text-sm text-gray-700">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )
          ) : (
            loadingRelated ? (
              <EarningsSkeleton />
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-5 shadow-[0_1px_2px_rgb(19_21_26/0.04)] grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-semibold text-gray-900">{earnings.total.toFixed(0)} TND</p>
                    <p className="text-xs text-gray-500">Revenus totaux</p>
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-900">{earnings.completedCount}</p>
                    <p className="text-xs text-gray-500">Locations terminées</p>
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-900">{earnings.allCount}</p>
                    <p className="text-xs text-gray-500">Locations au total</p>
                  </div>
                </div>

                {rentalsAsOwner.length === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-sm text-gray-500">Aucun revenu — cet utilisateur ne loue pas (encore) ses véhicules.</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl overflow-hidden shadow-[0_1px_2px_rgb(19_21_26/0.04)]">
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Véhicule</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Montant</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {rentalsAsOwner.map((r) => (
                          <tr key={r.id_rental}>
                            <td className="px-4 py-2.5 text-sm text-gray-700">{formatDateTime(r.start_date || r.created_at)}</td>
                            <td className="px-4 py-2.5 text-sm text-gray-700">{VEHICLE_TYPE_LABELS[r.vehicle_type] || r.vehicle_type || '—'}</td>
                            <td className="px-4 py-2.5 text-sm text-gray-700">{r.total_price ? `${r.total_price} TND` : '—'}</td>
                            <td className="px-4 py-2.5">
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                                r.status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                                r.status === 'active' ? 'bg-indigo-50 text-indigo-700' :
                                r.status === 'cancelled' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {r.status === 'completed' ? 'Terminé' : r.status === 'active' ? 'En cours' : r.status === 'cancelled' ? 'Annulé' : r.status === 'pending' ? 'À venir' : r.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
